import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { PlatformClient } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n/I18nProvider'
import { DateLisible, Nombre } from '../components/Nombre'
import { Badge, Button, Card, ConfirmDialog, DataTable, PageHeader, Stack, useToast } from '../components/ui'
import type { Column } from '../components/ui'

/**
 * Les clients de la plateforme : qui utilise le produit, sous quelle formule, et avec
 * quelle porte ouverte.
 *
 * POURQUOI LA CONFIRMATION DIT CE QU'ELLE DIT. Désactiver n'est pas ranger une fiche.
 * `core.find_user_for_sign_in` (migration 0003) porte `AND o.is_active` dans son WHERE, et
 * c'est la seule fonction autorisée à lire `core.app_user` hors cloisonnement — donc le
 * seul chemin d'ouverture de session qui existe. Basculer cette colonne ferme la session de
 * TOUT LE MONDE chez ce client, immédiatement. Cela se dit avant, pas après : une
 * confirmation qui demanderait « êtes-vous sûr ? » laisserait découvrir la conséquence par
 * l'appel du premier utilisateur enfermé dehors.
 *
 * POURQUOI « AUCUN ABONNEMENT » S'ÉCRIT EN TOUTES LETTRES. Les jointures de
 * `core.platform_list_clients` sont externes pour qu'un client sans abonnement apparaisse
 * quand même — c'est précisément celui qu'il faut réparer. Une cellule vide se lirait comme
 * un défaut d'affichage ; le mot écrit dit qu'il n'y a rien à afficher.
 */
export function ClientsPage() {
  const { t, describeError } = useI18n()
  const { session } = useAuth()
  const { notify } = useToast()

  const token = session?.token ?? ''

  const [clients, setClients] = useState<PlatformClient[]>([])
  const [loading, setLoading] = useState(true)
  // La cible de la confirmation : nulle tant qu'aucune bascule n'est demandée. Garder la
  // ligne entière, et non son seul identifiant, permet au message de nommer le client.
  const [cible, setCible] = useState<PlatformClient | null>(null)
  const [bascule, setBascule] = useState(false)

  const charger = useCallback(() => {
    if (token === '') return

    setLoading(true)

    api
      .listPlatformClients(token)
      .then(setClients)
      .catch((cause: unknown) => notify(describeError(cause), 'erreur'))
      .finally(() => setLoading(false))
  }, [token, notify, describeError])

  useEffect(charger, [charger])

  const confirmer = async () => {
    if (cible === null) return

    setBascule(true)

    try {
      await api.setPlatformClientActive(token, cible.organizationId, !cible.isActive)
      notify(t(cible.isActive ? 'platform.clients.deactivated' : 'platform.clients.activated'), 'succes')
      setCible(null)
      charger()
    } catch (cause: unknown) {
      notify(describeError(cause), 'erreur')
    } finally {
      setBascule(false)
    }
  }

  const colonnes: Column<PlatformClient>[] = [
    {
      key: 'legalName',
      header: t('platform.clients.legalName'),
      // CHAQUE LIGNE MÈNE À LA FICHE. La liste ne peut pas porter l'abonnement, les
      // activités et l'usage d'un client — et surtout pas les deux leviers côte à côte avec
      // le mot d'explication qui les distingue. Ces actes ont besoin d'un endroit où vivre.
      render: (c) => (
        <Link className="underline" to={`/platform/clients/${c.organizationId}`}>
          {c.legalName}
        </Link>
      ),
    },
    { key: 'governorate', header: t('platform.clients.governorate'), render: (c) => c.governorate ?? '—' },
    {
      key: 'plan',
      header: t('platform.clients.plan'),
      render: (c) =>
        c.planCode === null ? (
          // Pas de formule : un état lisible, pas une case vide.
          <span className="text-texte-doux">{t('platform.clients.noPlan')}</span>
        ) : (
          <span className="flex flex-wrap items-center gap-2">
            <span>{c.planName ?? c.planCode}</span>
            {c.subscriptionStatus !== null && (
              <Badge tone={c.subscriptionStatus === 'active' ? 'succes' : 'avertissement'}>
                {t(`platform.subscription.${c.subscriptionStatus}` as never)}
              </Badge>
            )}
          </span>
        ),
    },
    {
      key: 'farms',
      header: t('platform.clients.farms'),
      numeric: true,
      render: (c) => <Nombre valeur={c.farmCount} decimales={0} />,
    },
    {
      key: 'users',
      header: t('platform.clients.users'),
      numeric: true,
      render: (c) => <Nombre valeur={c.userCount} decimales={0} />,
    },
    {
      key: 'status',
      header: t('platform.clients.status'),
      render: (c) => (
        <Badge tone={c.isActive ? 'succes' : 'avertissement'}>
          {c.isActive ? t('common.active') : t('common.inactive')}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: t('platform.clients.createdAt'),
      render: (c) => <DateLisible valeur={c.createdAt} />,
    },
    {
      key: 'actions',
      header: t('common.actions'),
      render: (c) => (
        <span className="flex flex-wrap gap-2">
          <Link className="btn ghost" to={`/platform/clients/${c.organizationId}`}>
            {t('common.open')}
          </Link>
          <Button variant={c.isActive ? 'danger' : 'secondaire'} onClick={() => setCible(c)}>
            {c.isActive ? t('common.deactivate') : t('common.activate')}
          </Button>
        </span>
      ),
    },
  ]

  return (
    <Stack gap={4}>
      <PageHeader
        title={t('platform.clients.title')}
        description={t('platform.clients.description')}
        actions={
          <Link className="btn" to="/platform/clients/nouveau">
            {t('platform.clients.new')}
          </Link>
        }
      />

      <Card title={t('platform.clients.list')}>
        {loading ? (
          <p className="text-texte-doux">{t('common.loading')}</p>
        ) : (
          <DataTable
            columns={colonnes}
            rows={clients}
            rowKey={(c) => c.organizationId}
            emptyLabel={t('platform.clients.empty')}
          />
        )}
      </Card>

      <ConfirmDialog
        open={cible !== null}
        busy={bascule}
        title={t(cible?.isActive === true ? 'platform.clients.deactivate.title' : 'platform.clients.activate.title')}
        message={`${cible?.legalName ?? ''} — ${t(
          cible?.isActive === true ? 'platform.clients.deactivate.message' : 'platform.clients.activate.message',
        )}`}
        confirmLabel={t(cible?.isActive === true ? 'common.deactivate' : 'common.activate')}
        onConfirm={() => void confirmer()}
        onCancel={() => setCible(null)}
      />
    </Stack>
  )
}
